import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Autocomplete, Stack, TextField, Typography } from '@mui/material';
import { useColors } from 'frontend/hooks/useColors';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { useTranslation } from 'react-i18next';
import '../../styles/header.css';
import ErrorSnackbar from '../ErrorSnackbar';
import DatePickerFullScreenDialog from './DatePickerFullScreenDialog';
import DateRangeFilter from './DateRangeFilter';
import OrigenDestinoFullScreenDialog from './OrigenDestinoFullScreenDialog';
import PasajerosFullScreenDialog from './PasajerosFullScreenDialog';
import PasajerosSelector from './PasajerosSelector';
import './styles.css';

const PackageFilterForm = ({
  origins,
  destinations,
  handleOriginSelect,
  handleDestinationSelect,
  handleDateRangeSelect,
  handleOccupationChange,
  formInput,
  isDestinationEmpty,
  setIsDestinationEmpty,
  isOriginEmpty,
  setIsOriginEmpty,
  isDatesEmpty,
  isTuristasEmpty,
  setIsDatesEmpty,
  departureDate,
}) => {
  const { t } = useTranslation('translations');
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [focusedInput, setFocusedInput] = useState(null);

  const [adultsQuantity, setAdultsQuantity] = useState(1);
  const [totalTourists, setTotalTourists] = useState(1);

  const [isDestinationOpen, setIsDestinationOpen] = useState(false);
  const originInputRef = useRef(null);
  const destinationInputRef = useRef(null);
  const [selectedOrigin, setSelectedOrigin] = useState(formInput.origin ?? null);
  const [selectedDestination, setSelectedDestination] = useState(formInput.destination ?? null);
  const [isMobile, setIsMobile] = useState(false);
  const [isDestinationDialogOpen, setIsDestinationDialogOpen] = useState(false);
  const [isFullScreenRangeDatePickerOpen, setIsFullScreenRangeDatePickerOpen] = useState(false);
  const [shouldPasajerosOpen, setShouldPasajerosOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [openToast, setOpenToast] = useState(false);

  const maxPassengers = 3;

  const { background, text } = useColors();

  const getUniqueItemsArray = (data) => {
    if (!data) return [];
    return Object.values(
      data.reduce((acc, item) => {
        const normalizedName = item.name.trim().toLowerCase();
        if (!acc[normalizedName]) {
          acc[normalizedName] = { name: item.name.trim(), id: [item.id] };
        } else {
          acc[normalizedName].id.push(item.id);
        }
        return acc;
      }, {}),
    );
  };

  const [uniqueOriginsArray, uniqueDestinationsArray] = useMemo(
    () => [getUniqueItemsArray(origins?.data), getUniqueItemsArray(destinations?.data)],
    [origins?.data, destinations?.data],
  );

  const handleChangeOrigin = (value) => {
    handleOriginSelect(value);
    setSelectedOrigin(value);
    if (originInputRef.current) originInputRef.current.blur();

    if (destinationInputRef.current && value) {
      setTimeout(() => {
        destinationInputRef.current.focus();
      }, 200);
    }

    if (value) setIsDestinationOpen(true);
  };

  const handleChangeDestination = (value) => {
    handleDestinationSelect(value);
    setSelectedDestination(value);
    if (value) {
      setIsDestinationOpen(false);
      if (!startDate) {
        setFocusedInput('startDate');
      } else if (startDate && !endDate && !formInput.isOnlyOneWay) {
        setFocusedInput('endDate');
      }
    }
  };

  const injectCustomStyles = () => {
    const style = document.createElement('style');
    style.innerHTML = `
      .CalendarDay__selected_span {
        background: ${background}60 !important;
        color: ${text} !important;
      }
      .CalendarDay__hovered_span {
        background: ${background} !important;
      }
      .CalendarDay__selected {
        background: ${background} !important;
        color: ${text} !important;
        border: 1px solid ${text};
      }
      .CalendarDay__selected_span{
        border: none !important
      }
      .CalendarDay__hovered_span{
        border: none !important;
        background-color: ${background} !important
      }
      .DayPickerKeyboardShortcuts_show__bottomRight::before{
        display: none !important
      }
    `;
    document.head.appendChild(style);
  };

  const isDayBlocked = useCallback(
    (day) => {
      if (!departureDate) return false;

      const start = new Date(departureDate);
      start.setUTCDate(start.getUTCDate() - 1);

      const checkDay = new Date(Date.UTC(day.year(), day.month(), day.date()));

      return !(checkDay > start);
    },
    [departureDate],
  );

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setOpenToast(true);
  }, []);

  const handleCloseToast = useCallback(() => {
    setOpenToast(false);
  }, []);

  useEffect(() => {
    injectCustomStyles();
  }, []);

  useEffect(() => {
    if (formInput.dateSince && formInput.dateUntil) {
      setStartDate(moment(formInput.dateSince));
      setEndDate(moment(formInput.dateUntil));
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  }, []);

  useEffect(() => {
    if (departureDate) {
      const parsedDate = moment.utc(departureDate);
      setStartDate(parsedDate);
    }
  }, [departureDate, endDate]);

  useEffect(() => {
    if (startDate !== undefined && endDate !== undefined) {
      handleDateRangeSelect(startDate, endDate);
    }
    if (startDate && !endDate) {
      setFocusedInput('endDate');
    }
  }, [endDate, startDate]);

  useEffect(() => {
    if (startDate && endDate) setIsDatesEmpty(false);
  }, [startDate, endDate]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768 ? true : false);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setSelectedOrigin(formInput.origin ?? null);
  }, [formInput.origin]);

  useEffect(() => {
    setSelectedDestination(formInput.destination ?? null);
  }, [formInput.destination]);

  useEffect(() => {
    setTotalTourists(formInput?.adultsQty);
  }, []);

  useEffect(() => {
    if (adultsQuantity === maxPassengers) {
      showError(`El número máximo de adultos por paquete es ${maxPassengers}.`);
    }
    setTotalTourists(adultsQuantity);
  }, [adultsQuantity]);

  useEffect(() => {
    handleOccupationChange(totalTourists);
  }, [setTotalTourists, totalTourists]);

  const DateRangeInput = useCallback(
    () => (
      <DateRangeFilter
        {...{
          formInput,
          focusedInput,
          setFocusedInput,
          startDate,
          setStartDate,
          endDate,
          setEndDate,
          isFullScreenRangeDatePickerOpen,
          isDayBlocked,
          isMobile,
        }}
      />
    ),
    [
      formInput,
      focusedInput,
      setFocusedInput,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
      isFullScreenRangeDatePickerOpen,
      isDayBlocked,
      isMobile,
    ],
  );

  useEffect(() => {
    if (!selectedDestination) {
      setStartDate(null);
      setEndDate(null);
    }
  }, [selectedDestination]);

  return (
    <>
      <Stack className="input-container">
        <h3 style={{ fontSize: 12, color: '#4f4f4f' }}>¿Desde dónde?</h3>

        {isMobile ? (
          <OrigenDestinoFullScreenDialog
            onSelect={handleOriginSelect}
            options={uniqueOriginsArray}
            originSelected={formInput.origin}
            isOriginEmpty={isOriginEmpty}
            setIsOriginEmpty={setIsOriginEmpty}
            setIsDestinationDialogOpen={setIsDestinationDialogOpen}
          />
        ) : (
          <Autocomplete
            id="origin-autocomplete"
            className={`form__field origin-typeahead ${isOriginEmpty ? 'autocomplete-error' : ''}`}
            noOptionsText="No hay resultados"
            size="small"
            onChange={(_, value) => {
              handleChangeOrigin(value);
            }}
            value={selectedOrigin}
            defaultValue={formInput.origin}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            onFocus={() => setIsOriginEmpty(false)}
            options={uniqueOriginsArray}
            loading={origins?.isLoading}
            style={{ width: '100%' }}
            loadingText="Cargando orígenes"
            getOptionLabel={(option) => option.name}
            clearOnBlur
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={(input) => (originInputRef.current = input)}
                className="focus:ring-offset-0 focus:ring-0 ring-offset-0 ring-transparent"
                onFocus={() => {
                  const originInput = document.querySelector('.origin-typeahead');
                  if (originInput) {
                    const closeBtn = originInput.querySelector('.MuiAutocomplete-clearIndicator');
                    if (closeBtn) {
                      closeBtn.click();
                    }
                  }
                  handleOriginSelect(undefined);
                }}
              />
            )}
          />
        )}

        <Stack
          flexDirection={'row'}
          alignSelf={'flex-end'}
          alignItems={'center'}
          zIndex={isOriginEmpty ? 1 : -1}
        >
          <WarningAmberIcon style={{ fill: 'rgb(207, 24, 0)', height: 12, width: 12 }} />
          <Typography fontSize={11} color={'rgb(207, 24, 0)'}>
            Ingresa tu origen
          </Typography>
        </Stack>
      </Stack>

      <Stack className="input-container">
        <h3 style={{ fontSize: 12, color: '#4f4f4f' }}>¿Hasta dónde?</h3>

        {isMobile ? (
          <OrigenDestinoFullScreenDialog
            onSelect={handleChangeDestination}
            options={uniqueDestinationsArray}
            originSelected={formInput.destination}
            disabled={!formInput?.origin}
            isDestinationEmpty={isDestinationEmpty}
            setIsDestinationEmpty={setIsDestinationEmpty}
            isOriginEmpty={isOriginEmpty}
            setIsOriginEmpty={setIsOriginEmpty}
            isDestination
            isOpen={isDestinationDialogOpen}
            setIsDestinationDialogOpen={setIsDestinationDialogOpen}
            setIsFullScreenRangeDatePickerOpen={setIsFullScreenRangeDatePickerOpen}
          />
        ) : (
          <Autocomplete
            id="destination-autocomplete"
            className={`form__field destination-typeahead ${
              isDestinationEmpty ? 'autocomplete-error' : ''
            }`}
            noOptionsText="No hay resultados"
            loadingText="Cargando destinos"
            loading={destinations?.isLoading}
            onFocus={() => {
              setIsDestinationEmpty(false);
              setIsDestinationOpen(true);
            }}
            style={{ width: '100%' }}
            onChange={(_, value) => {
              handleChangeDestination(value);
            }}
            onBlur={() => setIsDestinationOpen(false)}
            size="small"
            defaultValue={formInput.destination}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            open={isDestinationOpen}
            disabled={!formInput?.origin}
            options={uniqueDestinationsArray}
            getOptionLabel={(option) => option.name}
            value={selectedDestination}
            clearOnBlur
            renderInput={(params) => (
              <TextField
                {...params}
                className="focus:ring-transparent"
                inputRef={(input) => (destinationInputRef.current = input)}
                onFocus={() => {
                  const input = document.querySelector('.destination-typeahead');
                  if (input) {
                    const closeBtn = input.querySelector('.MuiAutocomplete-clearIndicator');
                    if (closeBtn) {
                      closeBtn.click();
                    }
                  }
                }}
              />
            )}
          />
        )}

        <Stack
          flexDirection={'row'}
          alignSelf={'flex-end'}
          alignItems={'center'}
          zIndex={isDestinationEmpty ? 1 : -1}
          className="warning-animated-message"
        >
          <WarningAmberIcon style={{ fill: 'rgb(207, 24, 0)', height: 12, width: 12 }} />
          <Typography fontSize={11} color={'rgb(207, 24, 0)'}>
            Ingresa tu destino
          </Typography>
        </Stack>
      </Stack>

      <Stack className={`form__field date-picker ${isDatesEmpty ? 'dates-error' : ''}`}>
        <Stack flexDirection={'row'} justifyContent={'space-between'}>
          <Typography fontSize={12} color={'#4f4f4f'}>
            ¿Cuándo?
          </Typography>
        </Stack>

        {isMobile ? (
          <DatePickerFullScreenDialog
            isOpen={isFullScreenRangeDatePickerOpen}
            setShouldPasajerosOpen={setShouldPasajerosOpen}
            isDatesEmpty={isDatesEmpty}
            children={<DateRangeInput />}
            startDate={startDate}
            endDate={endDate}
          />
        ) : (
          <DateRangeInput />
        )}

        <Stack
          flexDirection={'row'}
          alignSelf={'flex-end'}
          alignItems={'center'}
          zIndex={isDatesEmpty ? 1 : -1}
        >
          <WarningAmberIcon style={{ fill: 'rgb(207, 24, 0)', height: 12, width: 12 }} />
          <Typography fontSize={11} color={'rgb(207, 24, 0)'}>
            Selecciona fechas
          </Typography>
        </Stack>
      </Stack>

      <Stack className="pasajeros-input-container">
        <Typography fontSize={12} color={'#4f4f4f'}>
          ¿Cuántos viajan?
        </Typography>

        <Stack>
          {isMobile ? (
            <PasajerosFullScreenDialog
              isPackage
              totalTourists={totalTourists}
              setTotalTourists={setTotalTourists}
              adultsQuantity={adultsQuantity}
              setAdultsQuantity={setAdultsQuantity}
              shouldOpen={shouldPasajerosOpen}
            />
          ) : (
            <PasajerosSelector
              isPackage
              adultsQuantity={adultsQuantity}
              isTuristasEmpty={isTuristasEmpty}
              setAdultsQuantity={setAdultsQuantity}
              totalTourists={totalTourists}
            />
          )}
        </Stack>
        <ErrorSnackbar
          open={openToast}
          message={errorMessage}
          onClose={handleCloseToast}
          severity="info"
        />
      </Stack>
    </>
  );
};

export default PackageFilterForm;
